import { Button, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";

import { useSelector } from "react-redux";
import DropdownComponent from "../../../../../App/Components/Dropdown/DropdownComponent";
import Signifier from "../../../../../App/Components/Signifier/Signifier";
import DraggableTable from "../../../../../App/Components/Table/DraggableTable";
import { Roles } from "../../../../../App/Enums/Roles";
import { SearchFormType } from "../../../../../App/Enums/SearchFormType";
import { basicSort } from "../../../../../App/Helpers/BasicSort";
import { callAPI } from "../../../../../App/Helpers/callApi";
import { CheckRoleForClient } from "../../../../../App/Helpers/PermissionsChecks";
import { selectCareersClientValue } from "../../../../../App/State/Slices/SelectedCareersClientSlice";
import { selectUserPermissionsValue } from "../../../../../App/State/Slices/UserPermissionsSlice";
import { editSearchForm, getSearchForm, syncSearchFormFromProduction } from "../../../SearchFormService";
import FloatingSearchFormTableCell from "./FloatingSearchFormTableCell";

function FloatingSearchFormPage() {
  const useStyles = makeStyles((theme) => ({
    root: {
      width: "80%",
      marginBottom: "50px",
      [theme.breakpoints.down("md")]: {
        width: "100%",
      },
    },
    error: {
      color: theme.palette.error.main,
    },
  }));

  const classes = useStyles();

  const selectCareersClient = useSelector(selectCareersClientValue);
  const rolesObject = useSelector(selectUserPermissionsValue);
  const [showSignifier, setShowSignifier] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [defaultRadius, setDefaultRadius] = useState(0);

  const columns = [
    { label: "Selected", propName: "selected" },
    { label: "Admin Text", propName: "searchPropertyAdminText" },
    { label: "Input Label", propName: "" },
    { label: "Input Placeholder", propName: "" },
    { label: "", propName: "" },
  ];

  const errorMessage = "Save failed. Any selected inputs must have Label and Placeholder values.";

  const getData = () => {
    setLoading(true);
    getSearchForm(selectCareersClient.id, SearchFormType.Float).then((response) => {
      setTemplates(response.templates);
      response.inputs?.sort((a, b) => {
        const order = basicSort(
          a.clientAdSearchFormConfigurations[0]?.order,
          b.clientAdSearchFormConfigurations[0]?.order
        );
        if (order !== 0) return order;
        return a[1] - b[1];
      });
      response.inputs?.forEach((row) => {
        row.selected = row.clientAdSearchFormConfigurations?.length > 0;
        row.label =
          row.clientAdSearchFormConfigurations[0]?.labelText === undefined
            ? ""
            : row.clientAdSearchFormConfigurations[0]?.labelText;
        row.placeholder =
          row.clientAdSearchFormConfigurations[0]?.placeholderText === undefined
            ? ""
            : row.clientAdSearchFormConfigurations[0]?.placeholderText;
        row.radiusCollections = response.radiusCollections;
        row.defaultRadius = response.defaultRadius;
      });
      setDefaultRadius(response.defaultRadius);
      setTableData(response.inputs);
      setLoading(false);
    });
  };

  function saveJobDetailDisplay() {
    var addProperties = [];
    var canSave = true;
    var newOrder = 1;
    var newDefaultRadius = defaultRadius;
    tableData.forEach((row) => {
      if (row.selected && row.label !== "" && row.placeholder !== "") {
        if (row.newRadius !== undefined) {
          newDefaultRadius = row.newRadius.item1;
        }
        let newProperty = {
          AvailableAdSearchFormInputId: row.id,
          ClientId: selectCareersClient.id,
          LabelText: row.label,
          PlaceholderText: row.placeholder,
          Order: newOrder,
          ClientSearchFormTypeId: SearchFormType.Float,
        };
        newOrder += 1;
        addProperties.push(newProperty);
      } else if (row.selected && (row.label === "" || row.placeholder === "")) {
        canSave = false;
      }
    });

    if (canSave === true) {
      let data = {
        ClientId: selectCareersClient.id,
        selectedInputs: addProperties,
        templateId: selectedTemplate.id,
        formType: SearchFormType.Float,
        defaultRadius: newDefaultRadius,
      };

      console.log(data);

      callAPI(
        editSearchForm(selectCareersClient.id, data),
        "Floating Search Form Saved",
        setShowSignifier,
        setResponseMessage,
        setShowError,
        setProcessing
      ).then(() => {
        getData();
      });
    } else {
      setShowSignifier(true);
      setShowError(true);
    }
  }

  function syncSearchForm() {
    if (loadingSync) return;
    setLoadingSync(true);
    callAPI(
      syncSearchFormFromProduction(selectCareersClient.id, SearchFormType.Float),
      "Floating Search Form Synced from Production",
      setShowSignifier,
      setResponseMessage,
      setShowError,
      setProcessing
    )
      .then(() => {
        getData();
      })
      .finally(() => {
        setLoadingSync(false);
      });
  }

  useEffect(() => {
    setTemplates([]);
    setTableData([]);
    if (selectCareersClient !== null) {
      getData();
    }
  }, [selectCareersClient]);

  return (
    <>
      <Signifier
        showSignifier={showSignifier}
        setShowSignifier={setShowSignifier}
        message={responseMessage}
        error={showError}
        errorMessage={errorMessage}
      />
      <div className={classes.root}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              sx={{
                marginBottom: "10px",
                marginRight: "20px",
              }}
              type="submit"
              variant="contained"
              color="primary"
              onClick={() => saveJobDetailDisplay()}
            >
              Save
            </Button>
            {import.meta.env.VITE_ENV === "Development" && (
              <Button
                sx={{
                  marginBottom: "10px",
                  marginRight: "20px",
                }}
                type="submit"
                variant="outlined"
                color="primary"
                onClick={() => syncSearchForm()}
                disabled={loadingSync}
              >
                Sync From Production
              </Button>
            )}
          </Grid>
          {CheckRoleForClient(rolesObject, selectCareersClient.id?.toUpperCase(), Roles.SuperAdmin) ? (
            <Grid item xs={12} sm={3}>
              <DropdownComponent
                currValue={selectedTemplate}
                items={templates}
                property="name"
                title="Assign To Template"
                handleChange={(value) => setSelectedTemplate(value)}
              />
            </Grid>
          ) : null}
          <DraggableTable
            columns={columns}
            tableData={tableData}
            setTableData={setTableData}
            rowComponent={FloatingSearchFormTableCell}
            loading={loading}
          />
        </Grid>
      </div>
    </>
  );
}

export default FloatingSearchFormPage;
